
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const isActive = (path: string) => location.pathname === path;

  const navLinks = [
    { path: '/', label: 'Inicio' },
    { path: '/servicios', label: 'Servicios' },
    { path: '/como-vender-tu-coche', label: 'Cómo Vender' },
    { path: '/blog', label: 'Blog' },
    { path: '/preguntas-frecuentes', label: 'FAQ' },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || isMobileMenuOpen ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <Link to="/" className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
              <img 
                src="https://public.readdy.ai/ai/img_res/d0431b70-5f6b-41b3-bc61-e00e5d5a8fd8.png" 
                alt="Desguace Gavá Logo" 
                className="h-9 sm:h-12 w-auto"
              />
              <span className={`text-base sm:text-xl font-bold transition-colors ${
                isScrolled || isMobileMenuOpen ? 'text-gray-900' : 'text-white'
              }`}>
                DESGUACE GAVÁ
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors whitespace-nowrap ${
                    isActive(link.path)
                      ? 'text-emerald-600'
                      : isScrolled
                      ? 'text-gray-700 hover:text-emerald-600'
                      : 'text-white hover:text-emerald-400'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Desktop CTA Buttons */}
            <div className="hidden md:flex items-center space-x-2 lg:space-x-3">
              <a
                href="tel:+34932932913"
                className="hidden lg:flex items-center space-x-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-medium transition-all hover:scale-105 whitespace-nowrap"
              >
                <i className="ri-phone-fill"></i>
                <span className="text-sm">Llamar</span>
              </a>
              <a
                href="https://wa.me/34932932913"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex items-center space-x-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-medium transition-all hover:scale-105 whitespace-nowrap"
              >
                <i className="ri-whatsapp-fill"></i>
                <span className="text-sm">WhatsApp</span>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer"
              aria-label="Menú"
            >
              <div className="relative w-6 h-5">
                <span 
                  className={`absolute left-0 w-6 h-0.5 transition-all duration-300 ${
                    isScrolled || isMobileMenuOpen ? 'bg-gray-900' : 'bg-white'
                  } ${isMobileMenuOpen ? 'top-2 rotate-45' : 'top-0'}`}
                ></span>
                <span 
                  className={`absolute left-0 top-2 w-6 h-0.5 transition-all duration-300 ${
                    isScrolled || isMobileMenuOpen ? 'bg-gray-900' : 'bg-white'
                  } ${isMobileMenuOpen ? 'opacity-0' : 'opacity-100'}`}
                ></span>
                <span 
                  className={`absolute left-0 w-6 h-0.5 transition-all duration-300 ${
                    isScrolled || isMobileMenuOpen ? 'bg-gray-900' : 'bg-white'
                  } ${isMobileMenuOpen ? 'top-2 -rotate-45' : 'top-4'}`}
                ></span>
              </div>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div 
        className={`fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity duration-300 ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
      ></div>

      {/* Mobile Menu Panel */}
      <div 
        className={`fixed top-16 sm:top-20 left-0 right-0 bg-white z-40 lg:hidden transition-all duration-300 shadow-xl ${
          isMobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
        }`}
      >
        <div className="max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                  isActive(link.path)
                    ? 'bg-emerald-50 text-emerald-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
          
          <div className="px-4 py-4 border-t border-gray-100 space-y-3">
            <a
              href="tel:+34932932913"
              className="flex items-center justify-center space-x-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-3 rounded-lg font-medium transition-all w-full"
            >
              <i className="ri-phone-fill text-lg"></i>
              <span>Llamar: 932 932 913</span>
            </a>
            <a
              href="https://wa.me/34932932913"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="flex items-center justify-center space-x-2 bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded-lg font-medium transition-all w-full"
            >
              <i className="ri-whatsapp-fill text-lg"></i>
              <span>WhatsApp Directo</span>
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
